<template>
  <aside class="ai-module-list" :class="`ai-module-list--${align}`">
    <div
      v-for="item in items"
      :key="item.title"
      class="module-item"
      :class="`module-item--${item.status}`"
    >
      <span class="module-item__icon">
        <svg
          v-if="item.status === 'done'"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
          />
        </svg>
      </span>
      <div class="module-item__copy">
        <strong>{{ item.title }}</strong>
        <span>{{ item.desc }}</span>
      </div>
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';

type ModuleStatus = 'done' | 'active' | 'pending';

interface ModuleItem {
  title: string;
  desc: string;
  status: ModuleStatus;
}

export default defineComponent({
  name: 'AiModuleList',
  props: {
    items: {
      type: Array as PropType<ModuleItem[]>,
      required: true,
    },
    align: {
      type: String as PropType<'left' | 'right'>,
      required: true,
    },
  },
});
</script>

<style scoped>
.ai-module-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.module-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  color: #5f7eb2;
}

.module-item::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 54px;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(153, 189, 255, 0.15),
    rgba(153, 189, 255, 0.72),
    rgba(153, 189, 255, 0.15)
  );
}

.ai-module-list--left .module-item::after {
  right: -40px;
}

.ai-module-list--right .module-item::after {
  left: -40px;
}

.module-item__icon {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(117, 171, 255, 0.52);
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 0 16px rgba(114, 216, 255, 0.15);
  flex: 0 0 auto;
}

.module-item__icon::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 10px currentColor;
}

.module-item__icon svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.module-item--done {
  color: #58b495;
}

.module-item--done .module-item__icon::before {
  display: none;
}

.module-item--active {
  color: #5b8cff;
}

.module-item--active .module-item__icon::before {
  animation: modulePulse 1.8s ease-in-out infinite;
}

.module-item--pending {
  color: #87a0cb;
}

.module-item__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.module-item__copy strong {
  color: #4063a3;
  font-size: 20px;
  font-weight: 600;
}

.module-item__copy span {
  font-size: 16px;
}

.ai-module-list--right .module-item {
  flex-direction: row-reverse;
  text-align: right;
}

@keyframes modulePulse {
  0%,
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.18);
    opacity: 1;
  }
}

@media (max-width: 1080px) {
  .module-item::after {
    display: none;
  }

  .ai-module-list {
    width: min(100%, 520px);
    margin: 0 auto;
  }

  .ai-module-list--right .module-item {
    flex-direction: row;
    text-align: left;
  }
}

@media (max-width: 768px) {
  .ai-module-list {
    gap: 16px;
  }

  .module-item__copy strong {
    font-size: 16px;
  }

  .module-item__copy span {
    font-size: 13px;
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .ai-module-list {
    gap: 8px;
    width: 100%;
  }

  .module-item {
    gap: 6px;
    align-items: flex-start;
  }

  .module-item__icon {
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }

  .module-item__icon::before {
    width: 5px;
    height: 5px;
  }

  .module-item__icon svg {
    width: 12px;
    height: 12px;
  }

  .module-item__copy {
    gap: 1px;
  }

  .module-item__copy strong {
    font-size: 10px;
    line-height: 1.25;
  }

  .module-item__copy span {
    font-size: 9px;
    line-height: 1.2;
  }
}

@media (orientation: landscape) and (max-height: 820px) {
  .ai-module-list {
    gap: 12px;
  }

  .module-item {
    gap: 10px;
  }

  .module-item__icon {
    width: 22px;
    height: 22px;
  }

  .module-item__icon::before {
    width: 6px;
    height: 6px;
  }

  .module-item__copy strong {
    font-size: 14px;
  }

  .module-item__copy span {
    font-size: 11px;
  }
}
</style>
